import React, { useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import { api } from '../../../api/remembrApi';
import type { ItemLocationEvent } from '../../../api/types';

/**
 * LocationHistoryTimeline — Phase 3F (closes #117).
 *
 * Read-only timeline of every location-change event for an item.
 * Backed by `GET /inventory/{item_id}/location-history` (backend#144).
 * Renders below CustodySection on ItemDetail.
 *
 * Feature-detection: if the backend returns 404 the section hides
 * cleanly (older deploys without BE#144). Other errors render an
 * inline retry banner.
 */

const PREVIEW_LIMIT = 5;
const EXPANDED_LIMIT = 20;

interface LocationHistoryTimelineProps {
  itemId: number;
}

/** Maps backend source string → display icon + label. Forward-compatible
 *  with future sources; unknown values get a neutral fallback. */
function describeSource(source: string): {
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  tint: string;
} {
  switch (source) {
    case 'SCAN':
      return { icon: 'qr-code-scanner', label: 'Initial scan', tint: fpColors.tertiary };
    case 'GPS_BUTTON':
      return { icon: 'my-location', label: 'GPS button', tint: fpColors.primary };
    case 'ADDRESS_PICKER':
      return { icon: 'place', label: 'Address picked', tint: fpColors.primary };
    case 'MANUAL_EDIT':
      return { icon: 'edit', label: 'Manual edit', tint: fpColors.onSurfaceVariant };
    case 'REVERSE_GEOCODE':
      return {
        icon: 'auto-fix-high',
        label: 'Address resolved',
        tint: fpColors.outline,
      };
    default:
      return { icon: 'history', label: source, tint: fpColors.outline };
  }
}

/** Human-readable timestamp. Today → "3:42 PM"; yesterday → "Yesterday, 11:20 AM";
 *  older → "Apr 28 · 11:20 AM". */
function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear()
      && d.getMonth() === now.getMonth()
      && d.getDate() === now.getDate();
    const time = d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
    if (sameDay) return `Today, ${time}`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (
      d.getFullYear() === yesterday.getFullYear()
      && d.getMonth() === yesterday.getMonth()
      && d.getDate() === yesterday.getDate()
    ) {
      return `Yesterday, ${time}`;
    }
    return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${time}`;
  } catch {
    return iso;
  }
}

/** One-line address summary. Prefers structured city/state, falls back
 *  to the flat address string, then to lat/lng. */
function formatAddress(ev: ItemLocationEvent): string | null {
  const cityState = [ev.city, ev.state].filter(Boolean).join(', ');
  if (cityState) return cityState;
  if (ev.address) return ev.address;
  if (typeof ev.lat === 'number' && typeof ev.lng === 'number') {
    return `${ev.lat.toFixed(4)}, ${ev.lng.toFixed(4)}`;
  }
  return null;
}

export const LocationHistoryTimeline: React.FC<LocationHistoryTimelineProps> = ({
  itemId,
}) => {
  const [events, setEvents] = useState<ItemLocationEvent[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 404 → backend doesn't have BE#144 yet. Hide the section silently.
  const [unsupported, setUnsupported] = useState(false);

  const fetchHistory = async (limit: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getLocationHistory(itemId, { limit });
      setEvents(res.events ?? []);
      setTotal(res.total ?? 0);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setUnsupported(true);
        return;
      }
      const detailRaw = err?.response?.data?.detail;
      const detail = Array.isArray(detailRaw)
        ? detailRaw.map((d: any) => d?.msg || JSON.stringify(d)).join(', ')
        : typeof detailRaw === 'string'
          ? detailRaw
          : null;
      setError(detail || err?.message || 'Could not load history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!itemId) return;
    fetchHistory(PREVIEW_LIMIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const visibleEvents = useMemo(() => events ?? [], [events]);
  const hasMore = total > visibleEvents.length;

  if (unsupported) return null;

  // Hide the section entirely if zero events and not loading — older
  // items without any recorded history don't deserve a dead surface.
  if (!loading && (events?.length ?? 0) === 0 && !error) return null;

  return (
    <View style={styles.card} testID="location-history-timeline">
      <View style={styles.header}>
        <MaterialIcons name="location-history" size={18} color={fpColors.primary} />
        <Text style={styles.title}>Location history</Text>
        {total > 0 ? (
          <View style={styles.countPill}>
            <Text style={styles.countPillText}>{total}</Text>
          </View>
        ) : null}
      </View>

      {loading && !events ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={fpColors.primary} />
          <Text style={styles.loadingText}>Loading history…</Text>
        </View>
      ) : null}

      {error ? (
        <View style={styles.errorBanner}>
          <MaterialIcons
            name="error-outline"
            size={16}
            color={fpColors.error}
          />
          <Text style={styles.errorText}>{error}</Text>
          <Pressable
            onPress={() => fetchHistory(expanded ? EXPANDED_LIMIT : PREVIEW_LIMIT)}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel="Retry loading history"
            style={styles.retryBtn}
          >
            <Text style={styles.retryText}>Retry</Text>
          </Pressable>
        </View>
      ) : null}

      {visibleEvents.length > 0 ? (
        <View style={styles.timeline}>
          {visibleEvents.map((ev, idx) => {
            const desc = describeSource(ev.source);
            const addr = formatAddress(ev);
            const isLast = idx === visibleEvents.length - 1;
            return (
              <View
                key={ev.id}
                style={styles.row}
                testID={`location-history-row-${ev.id}`}
              >
                <View style={styles.timelineRail}>
                  <View style={[styles.dot, { backgroundColor: desc.tint }]}>
                    <MaterialIcons
                      name={desc.icon}
                      size={11}
                      color="#FFFFFF"
                    />
                  </View>
                  {!isLast ? <View style={styles.line} /> : null}
                </View>
                <View style={styles.rowBody}>
                  <View style={styles.rowHeaderLine}>
                    <Text style={styles.rowTimestamp}>
                      {formatTimestamp(ev.created_at)}
                    </Text>
                    {ev.actor_name ? (
                      <Text style={styles.rowActor}>· {ev.actor_name}</Text>
                    ) : null}
                  </View>
                  <Text style={[styles.rowSource, { color: desc.tint }]}>
                    {desc.label}
                  </Text>
                  {ev.text ? (
                    <Text style={styles.rowText} numberOfLines={1}>
                      {ev.text}
                    </Text>
                  ) : null}
                  {addr ? (
                    <Text style={styles.rowAddress} numberOfLines={2}>
                      {addr}
                    </Text>
                  ) : null}
                  {ev.note ? (
                    <Text style={styles.rowNote} numberOfLines={2}>
                      &ldquo;{ev.note}&rdquo;
                    </Text>
                  ) : null}
                </View>
              </View>
            );
          })}
        </View>
      ) : null}

      {!expanded && hasMore ? (
        <Pressable
          onPress={() => {
            setExpanded(true);
            fetchHistory(EXPANDED_LIMIT);
          }}
          accessibilityRole="button"
          accessibilityLabel={`Show all ${total} location events`}
          testID="location-history-show-all"
          style={({ pressed }) => [
            styles.expandBtn,
            pressed && { opacity: 0.85 },
          ]}
        >
          <Text style={styles.expandBtnText}>
            Show all {total} events
          </Text>
          <MaterialIcons
            name="expand-more"
            size={16}
            color={fpColors.primary}
          />
        </Pressable>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    flex: 1,
  },
  countPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: fpColors.primaryContainer,
  },
  countPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onPrimaryContainer,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
  },
  loadingText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: `${fpColors.errorContainer}40`,
    borderRadius: 10,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.error,
  },
  retryBtn: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: fpColors.error,
    borderRadius: 8,
  },
  retryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onPrimary,
  },
  timeline: { gap: 14 },
  row: { flexDirection: 'row', gap: 10 },
  timelineRail: {
    width: 22,
    alignItems: 'center',
  },
  dot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
  },
  line: {
    flex: 1,
    width: 2,
    backgroundColor: fpColors.outlineVariant,
    marginTop: 2,
  },
  rowBody: { flex: 1, paddingBottom: 4, gap: 2 },
  rowHeaderLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  rowTimestamp: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onSurface,
  },
  rowActor: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  rowSource: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  rowText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
    marginTop: 2,
  },
  rowAddress: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  rowNote: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    fontStyle: 'italic',
    color: fpColors.onSurfaceVariant,
  },
  expandBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
  },
  expandBtnText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.primary,
  },
});

export default LocationHistoryTimeline;
